import { createThrottle, getServiceData, getServiceNameFromUrl, initGetServiceData } from '../services';
const defaultAttemptLimit = 3;
export const addCsrfHeader = config => {
  const serviceName = getServiceNameFromUrl(config.url);
  if (serviceName) {
    const csrfToken = getServiceData(serviceName)?.csrfToken;
    if (csrfToken) {
      config.headers[csrfToken.headerName] = csrfToken.value;
    }
  }
  return config;
};
export const captureCsrfHeader = response => {
  const config = response.config;
  const serviceName = getServiceNameFromUrl(config.url);
  // Axios uses lowercase for header names
  const csrfHeaderName = response.headers['x-csrf-header'];
  const csrfToken = csrfHeaderName ? response.headers[csrfHeaderName.toLowerCase()] : null;

  // Need a service and a token to do anything.
  if (serviceName && csrfToken) {
    const serviceData = initGetServiceData(serviceName);
    serviceData.csrfToken = {
      headerName: csrfHeaderName,
      value: csrfToken
    };
  }
  return response;
};
export const createCsrfRetryHandler = axios => err => {
  const status = err.response?.status;
  const is403ForCsrf = status === 403 && err.response?.headers['x-forbidden-reason'] === 'CSRF';
  const is401ForCsrf = status === 401 && err.response?.headers['x-unauthorized-reason'] === 'POSSIBLE_CSRF';
  if (!is403ForCsrf && !is401ForCsrf && status !== 449) {
    return Promise.reject(err);
  }

  // Not a service for this to handle, let the error continue.
  const serviceName = getServiceNameFromUrl(err.config.url);
  if (!serviceName) {
    return Promise.reject(err);
  }
  const serviceData = initGetServiceData(serviceName);
  const csrfHeaderName = err.response?.headers['x-csrf-header'];
  // Lowercase the header name because Axios makes all headers lowercase
  const csrfToken = csrfHeaderName ? err.response?.headers[csrfHeaderName.toLowerCase()] : null;

  // Not all CSRF responses include the token. Avoid updating token to null.
  if (csrfToken) {
    serviceData.csrfToken = {
      headerName: csrfHeaderName,
      value: csrfToken
    };
  }

  // Without a CSRF token there is no point in retrying.
  if (is403ForCsrf && !serviceData.csrfToken) {
    return Promise.reject(err);
  }

  // An existing throttle is completed, and the service is effectively saying we are no longer authenticated.
  // Reenable the throttle or we can end up with multiple requests retrying to re-authencate.
  if (is401ForCsrf && serviceData.throttle?.complete) {
    err.config.__throttleId = createThrottle(serviceName);
  }

  // We already tried with the expected CSRF header enough times, stop retrying.
  const previousAttempts = err.config.csrfRetryCount ?? 0;
  const attemptsLimit = err.config.csrfRetryLimit ?? defaultAttemptLimit;
  if (previousAttempts >= attemptsLimit) {
    return Promise.reject(err);
  }
  err.config.csrfRetryCount = previousAttempts + 1;

  // Only the request that created the throttled should skip it. It's the request that others are waiting on to complete the throttle.
  if (serviceData.throttle?.throttleId === err.config.__throttleId) {
    err.config.disableNovaServiceThrottle = true;
  }
  return axios.request(err.config);
};
//# sourceMappingURL=csrf.js.map